/**
 * The assistant API owns the composer shape through `ui_hints`. Keeping this
 * translation in one place prevents live responses and history replay from
 * choosing different inputs for the same prompt.
 */

export interface InputHints {
  placeholder?: string;
  /** Whether the paperclip and drag/drop affordances should be available. */
  allowUpload: boolean;
  /** Whether pasted text should use the document editor and render as a card. */
  documentMode: boolean;
}

interface AssistantUIHintsLike {
  placeholder?: unknown;
  show_text_area?: unknown;
  show_upload?: unknown;
}

export interface AssistantComposerConfig {
  wantsComposer: boolean;
  inputHints: InputHints | null;
}

export const getAssistantComposerConfig = (
  uiHints?: AssistantUIHintsLike | null,
): AssistantComposerConfig => {
  const wantsTextArea = uiHints?.show_text_area === true;
  const wantsUpload = uiHints?.show_upload === true;
  const wantsComposer = wantsTextArea || wantsUpload;

  if (!wantsComposer) {
    return { wantsComposer: false, inputHints: null };
  }

  return {
    wantsComposer: true,
    inputHints: {
      ...(typeof uiHints?.placeholder === 'string'
        ? { placeholder: uiHints.placeholder }
        : {}),
      allowUpload: wantsUpload,
      documentMode: wantsUpload,
    },
  };
};

const LEGACY_DOCUMENT_STATES = new Set(['upload_jd', 'get_jd_upload']);

/**
 * Flow 3 state names are server-driven, so document mode comes from the API.
 * The state fallback only preserves older hiring-flow JD prompts.
 */
export const shouldUseDocumentComposer = (
  conversationState?: string,
  documentMode = false,
): boolean => documentMode || LEGACY_DOCUMENT_STATES.has(conversationState ?? '');
